import Image from 'next/image'
import { Globe, Mail, MessageCircleQuestion, Phone, Store } from 'lucide-react'

/**
 * Per-channel branding, shared by every surface that lists conversations. Real
 * platforms (Telegram, Instagram, Facebook) render their brand logo from
 * `/public/images/channels/*.svg` (brand-coloured, see `public/images/channels`);
 * generic channels (our own web widget / demo, email, phone) have no brand, so
 * they keep a neutral lucide glyph with a distinguishing colour.
 *
 * `channelType` is a plain string rather than a messaging `ChannelType`: the
 * unified inbox draws email and phone rows through the same component, and a
 * shared component must not depend on one domain's union.
 */
type ChannelConfig =
  | { kind: 'logo'; src: string; label: string }
  | { kind: 'icon'; icon: typeof Globe; label: string; className: string }

const CHANNEL_CONFIG: Record<string, ChannelConfig> = {
  instagram: { kind: 'logo', src: '/images/channels/instagram.svg', label: 'Instagram' },
  facebook: { kind: 'logo', src: '/images/channels/facebook.svg', label: 'Facebook' },
  telegram: { kind: 'logo', src: '/images/channels/telegram.svg', label: 'Telegram' },
  whatsapp: { kind: 'logo', src: '/images/channels/whatsapp.svg', label: 'WhatsApp' },
  tiktok: { kind: 'logo', src: '/images/channels/tiktok.svg', label: 'TikTok' },
  web_widget: { kind: 'icon', icon: Globe, label: 'Web Widget', className: 'text-gray-500' },
  // Distinct icon + colour so demo conversations are visibly different
  // from normal web_widget rows in the inbox list. Storefront icon ties
  // semantically to the Shopify demo domain the visitor picked.
  web_demo: { kind: 'icon', icon: Store, label: 'Web Demo', className: 'text-amber-500' },
  email: { kind: 'icon', icon: Mail, label: 'Email', className: 'text-sky-600' },
  phone: { kind: 'icon', icon: Phone, label: 'Phone', className: 'text-emerald-600' },
}

interface ChannelIconProps {
  channelType: string
  size?: number
  className?: string
}

export function ChannelIcon({ channelType, size = 16, className = '' }: ChannelIconProps) {
  const config = CHANNEL_CONFIG[channelType]

  // An unknown channel says so. Drawing it as the web widget was the previous
  // behaviour and it labelled a channel we do not recognise as one we do.
  if (!config) {
    return (
      <span title={channelType || 'Unknown channel'}>
        <MessageCircleQuestion size={size} className={`text-muted-foreground ${className}`} />
      </span>
    )
  }

  if (config.kind === 'logo') {
    return (
      <span title={config.label} className="inline-flex shrink-0">
        <Image
          src={config.src}
          alt={`${config.label} logo`}
          width={size}
          height={size}
          className={className}
        />
      </span>
    )
  }

  const Icon = config.icon
  return (
    <span title={config.label}>
      <Icon size={size} className={`${config.className} ${className}`} />
    </span>
  )
}
